import { MenuIcon, MenuOpenIcon, Paragraph, QuestionmarkIcon } from '@flysas-tech/react-components';
import { NavLink, useLocation } from '@remix-run/react';
import { useState } from 'react';
// eslint-disable-next-line n/no-missing-import
import * as tentacle from 'virtual:octopus/tentacle-sidebar';

import OctopusLogo from '#components/octopus-logo.jsx';
import { Switcher, SwitcherOption } from '#components/switcher.jsx';

const context = tentacle.context;

const tentacleLinks = [
  { title: 'Octopus', displayName: 'Octopus', route: '/', logo: 'OctopusLogo' },
  ...Object.keys(context).map(tentacleName => ({
    title: tentacleName,
    route: '/' + tentacleName,
    logo: context[tentacleName].logoName,
    displayName: context[tentacleName].displayName || context[tentacleName].title,
  })),
];

function getNavLinkClasses({ isActive }) {
  return `flex gap-4 p-3 h-12 items-center rounded-lg overflow-hidden ${
    isActive ? 'bg-blue-40 hover:bg-grey-70' : 'bg-white hover:bg-grey-90'
  }`;
}

/**
 * NavigationLink component.
 * @param {object} props - The component props.
 * @param {{ route: string, icon: string, title: string }} props.link - The link object.
 * @param {boolean} props.open - Whether the sidebar is open.
 * @returns {import('react').ReactElement<Props>} - The NavigationLink component.
 */
function NavigationLink({ link, open }) {
  const Icon = link.icon ? tentacle[link.icon] : QuestionmarkIcon;
  const { pathname } = useLocation();

  return (
    <li key={link}>
      <NavLink
        to={link.route}
        className={getNavLinkClasses}
        prefetch={pathname === link.route ? 'none' : 'viewport'}
        end
        viewTransition
      >
        {({ isActive }) => (
          <>
            <span>
              <Icon color={isActive ? 'white' : 'blue-30'} size="md" />
            </span>
            <Paragraph
              className={`${isActive ? 'text-white' : 'text-blue-30'} ${
                open ? 'opacity-100' : 'opacity-0'
              } transition-opacity duration-300 ease-in-out`}
            >
              {link.title}
            </Paragraph>
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * @param {import('@adg-octopus/functions/middlewares/auth.js').User} user user object
 * @returns {Function} sidebarFilter
 */
function createSidebarFilter(user) {
  /**
   * @param {{ title: string }} remote title
   * @returns {boolean} whether the link should be shown in the sidebar
   */
  return function sidebarFilter(remote) {
    if (remote.title === 'Octopus') {
      return true;
    }
    return context[remote.title]?.access || user?.isExternal
      ? context[remote.title]?.access?.some(access => user?.accesses?.includes(access))
      : true;
  };
}

/**
 * @param {import('@adg-octopus/functions/middlewares/auth.js').User} user current user object
 * @param {string} tentacle current tentacle
 * @returns {import('react').ReactElement} navigation links
 */
const getNavigationLinks = (user, tentacle) =>
  context[tentacle]?.links.filter(({ access }) =>
    access && Array.isArray(access) ? access.some(access => user?.accesses?.includes(access)) : true
  ) ?? [];

/**
 * @param {import('@adg-octopus/functions/middlewares/auth.js').User} user current user object
 * @param {string} tentacle current tentacle
 * @returns {boolean} whether the user has access to the page
 */
const hasAccessToPage = (user, tentacle) => {
  return context[tentacle]?.access ? context[tentacle]?.access?.some(access => user?.accesses?.includes(access)) : true;
};

/**
 * Sidebar component.
 * @param {object} props - The component props.
 * @param {import('@adg-octopus/functions/middlewares/auth.js').User} props.user - The user object.
 * @param {boolean} [props.open] - Whether the sidebar is open.
 * @param {Function} [props.onToggle] - The toggle function. Fired when sidebar changes state
 * @returns {import('react').ReactElement<{ user: import('@adg-octopus/functions/middlewares/auth.js').User }>} - The Sidebar component.
 */
export default function Sidebar({ onToggle, open: isSidebarOpen, user }) {
  const { pathname } = useLocation();
  const currentTentacle = Object.keys(context).find(tentacle => pathname.startsWith('/' + tentacle));
  const sidebarFilter = createSidebarFilter(user);
  const switcherOptions = tentacleLinks.filter(sidebarFilter);
  const [selectedRemote, setSelectedRemote] = useState(
    switcherOptions.find(link => pathname === link.route) ?? switcherOptions[0]
  );
  const navigationLinks = hasAccessToPage(user, currentTentacle) ? getNavigationLinks(user, currentTentacle) : [];

  function toggleSidebar() {
    onToggle?.();
  }

  function handleRouteChange(route) {
    setSelectedRemote(route);
    const mobile = window.innerWidth < 768; // TW md breakpoint, could probably be improved
    if (mobile) {
      toggleSidebar();
    }
  }

  return (
    <aside
      className={`flex flex-col ${
        isSidebarOpen ? 'md:w-60 w-full px-2' : 'w-0 md:w-16 px-0 overflow-hidden'
      } h-full md:px-2 py-4 bg-white sticky top-0 transition-all duration-300 ease-in-out`}
    >
      <button
        className={`flex cursor-pointer ${isSidebarOpen ? 'justify-end' : 'justify-center'}`}
        onClick={toggleSidebar}
        type="button"
      >
        {isSidebarOpen ? <MenuOpenIcon color="black" /> : <MenuIcon color="black" />}
      </button>
      <nav className="flex flex-col pt-9">
        <Switcher
          selected={selectedRemote}
          logo={tentacle[selectedRemote.logo] || OctopusLogo}
          expanded={isSidebarOpen}
          setExpanded={toggleSidebar}
          className="flex-1"
        >
          {switcherOptions
            .filter(remote => remote.title !== selectedRemote.title)
            .map(remote => (
              <SwitcherOption
                key={remote.route}
                logo={tentacle[remote.logo] || OctopusLogo}
                expanded={isSidebarOpen}
                displayName={remote.displayName}
                link={remote}
                onClickChangeSelected={handleRouteChange}
              />
            ))}
        </Switcher>
        <ul className="flex flex-col gap-2 list-none pt-3 overflow-y-auto scrollbar-hide">
          {navigationLinks.map(link => (
            <NavigationLink key={link.route} link={link} open={isSidebarOpen} />
          ))}
        </ul>
      </nav>
    </aside>
  );
}
